import type {
  FilterDisplayComp,
  FilterDisplayParams,
  FilterHandler,
  DoesFilterPassParams,
} from "ag-grid-enterprise";

interface StatusTestModel {
  filterType: "statusTest";
  statuses?: string[];
  test?: "Test" | "Not test";
  voteBoxes?: false;
}

interface StatusTestFilterParams extends FilterDisplayParams<unknown, unknown, StatusTestModel> {
  statuses?: string[];
  statusCountsUrl?: string;
  // Vote boxes are a row type, not a status — hide the toggle on grids that never hold box rows
  // (a vote box's own detail grid only ever lists that box's elections).
  showVoteBoxes?: boolean;
}

interface StatusCountsResponse {
  counts?: Record<string, number>;
  test_count?: number;
  vote_box_count?: number;
}

export class StatusTestFilter implements FilterDisplayComp<unknown, unknown, StatusTestModel> {
  private eGui!: HTMLElement;
  private params!: StatusTestFilterParams;
  private statusOptions: string[] = [];
  private statusInputs: HTMLInputElement[] = [];
  private statusTextSpans: HTMLSpanElement[] = [];
  private testTextSpan!: HTMLSpanElement;
  private statusCounts: Record<string, number> | null = null;
  private testCount: number | null = null;
  private voteBoxTextSpan: HTMLSpanElement | null = null;
  private voteBoxCount: number | null = null;
  private selectAllInput!: HTMLInputElement;
  private includeTestInput!: HTMLInputElement;
  private includeVoteBoxesInput: HTMLInputElement | null = null;
  private showVoteBoxes = true;

  init(params: StatusTestFilterParams): void {
    this.params = params;
    this.statusOptions = params.statuses ?? [];
    this.showVoteBoxes = params.showVoteBoxes !== false;
    this.eGui = document.createElement("div");
    this.eGui.className = "ag-grid-status-test-filter";
    this.build();
    this.renderFromModel(params.model);
    this.fetchStatusCounts();
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  refresh(params: StatusTestFilterParams): boolean {
    this.params = params;
    this.renderFromModel(params.model);
    return true;
  }

  private build(): void {
    this.statusTextSpans = [];
    const statusSection = document.createElement("div");
    statusSection.className = "ag-grid-status-test-filter__section";

    const allRow = document.createElement("label");
    allRow.className = "ag-grid-status-test-filter__option ag-grid-status-test-filter__option--all";
    this.selectAllInput = document.createElement("input");
    this.selectAllInput.type = "checkbox";
    this.selectAllInput.addEventListener("change", () => {
      const checked = this.selectAllInput.checked;
      this.selectAllInputs().forEach((input) => (input.checked = checked));
      this.emitChange();
      this.syncSelectAll();
    });
    const allText = document.createElement("span");
    allText.textContent = "(Select All)";
    allRow.appendChild(this.selectAllInput);
    allRow.appendChild(allText);
    statusSection.appendChild(allRow);

    this.statusInputs = this.statusOptions.map((label) => {
      const row = document.createElement("label");
      row.className = "ag-grid-status-test-filter__option";
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.value = label;
      checkbox.addEventListener("change", () => {
        this.emitChange();
        this.syncSelectAll();
      });
      const text = document.createElement("span");
      text.textContent = label;
      this.statusTextSpans.push(text);
      row.appendChild(checkbox);
      row.appendChild(text);
      statusSection.appendChild(row);
      return checkbox;
    });

    if (this.showVoteBoxes) {
      const voteBoxRow = document.createElement("label");
      voteBoxRow.className = "ag-grid-status-test-filter__option";
      this.includeVoteBoxesInput = document.createElement("input");
      this.includeVoteBoxesInput.type = "checkbox";
      this.includeVoteBoxesInput.addEventListener("change", () => {
        this.emitChange();
        this.syncSelectAll();
      });
      const voteBoxText = document.createElement("span");
      voteBoxText.textContent = "Vote box";
      this.voteBoxTextSpan = voteBoxText;
      voteBoxRow.appendChild(this.includeVoteBoxesInput);
      voteBoxRow.appendChild(voteBoxText);
      statusSection.appendChild(voteBoxRow);
    }

    const divider = document.createElement("div");
    divider.className = "ag-grid-status-test-filter__divider";

    const testSection = document.createElement("div");
    testSection.className = "ag-grid-status-test-filter__section";
    const testRow = document.createElement("label");
    testRow.className = "ag-grid-status-test-filter__option";
    this.includeTestInput = document.createElement("input");
    this.includeTestInput.type = "checkbox";
    // syncSelectAll() as well as emitChange(): Test now counts toward "(Select All)"
    // (see selectAllInputs), so toggling it alone has to refresh that tri-state too.
    this.includeTestInput.addEventListener("change", () => {
      this.emitChange();
      this.syncSelectAll();
    });
    const testText = document.createElement("span");
    testText.textContent = "Test votes";
    this.testTextSpan = testText;
    testRow.appendChild(this.includeTestInput);
    testRow.appendChild(testText);
    testSection.appendChild(testRow);

    this.eGui.appendChild(statusSection);
    this.eGui.appendChild(divider);
    this.eGui.appendChild(testSection);
  }

  private renderFromModel(model: StatusTestModel | null): void {
    const statuses = model?.statuses;
    const allChecked = statuses === undefined;
    const selected = new Set(statuses ?? []);
    this.statusInputs.forEach((input) => {
      input.checked = allChecked || selected.has(input.value);
    });

    this.includeTestInput.checked = model?.test !== "Not test";

    if (this.includeVoteBoxesInput) this.includeVoteBoxesInput.checked = model?.voteBoxes !== false;

    this.syncSelectAll();
  }

  private fetchStatusCounts(): void {
    const url = this.params.statusCountsUrl;
    if (!url) return;

    fetch(`${url}?column=status`, { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : { counts: {}, test_count: undefined, vote_box_count: undefined }))
      .then((data: StatusCountsResponse) => {
        this.statusCounts = data.counts ?? {};
        this.testCount = data.test_count ?? null;
        this.voteBoxCount = data.vote_box_count ?? null;
        this.applyStatusCounts();
        this.applyTestCount();
        this.applyVoteBoxCount();
      })
      .catch(() => {
      });
  }

  private applyStatusCounts(): void {
    if (!this.statusCounts) return;
    const counts = this.statusCounts;

    this.statusTextSpans.forEach((span, index) => {
      const label = this.statusOptions[index];
      const count = counts[label];
      span.textContent = count === undefined ? label : `${label} (${count})`;
    });
  }

  private applyTestCount(): void {
    if (this.testCount === null) return;

    this.testTextSpan.textContent = `Test votes (${this.testCount})`;
  }

  private applyVoteBoxCount(): void {
    if (this.voteBoxCount === null || !this.voteBoxTextSpan) return;

    this.voteBoxTextSpan.textContent = `Vote box (${this.voteBoxCount})`;
  }

  // "(Select All)" must cover every dimension this popup can restrict — statuses, Vote box,
  // and Test — so checking it clears every restriction (computeModel() -> null) and the
  // tri-state accurately reflects a lone unchecked Test box (D-2.1: Test stays its own visual
  // section but participates in Select All, same as the Vote box toggle already does).
  private selectAllInputs(): HTMLInputElement[] {
    const base = [...this.statusInputs, this.includeTestInput];
    if (!this.includeVoteBoxesInput) return base;

    return [...base, this.includeVoteBoxesInput];
  }

  private syncSelectAll(): void {
    const inputs = this.selectAllInputs();
    const checkedCount = inputs.filter((i) => i.checked).length;
    this.selectAllInput.checked = checkedCount === inputs.length;
    this.selectAllInput.indeterminate = checkedCount > 0 && checkedCount < inputs.length;
  }

  private emitChange(): void {
    this.params.onModelChange(this.computeModel());
  }

  private computeModel(): StatusTestModel | null {
    const selected = this.statusInputs.filter((i) => i.checked).map((i) => i.value);
    const allSelected = selected.length === this.statusInputs.length;
    const excludeTest = !this.includeTestInput.checked;
    const excludeVoteBoxes = this.includeVoteBoxesInput ? !this.includeVoteBoxesInput.checked : false;

    if (allSelected && !excludeTest && !excludeVoteBoxes) return null;

    const model: StatusTestModel = { filterType: "statusTest" };
    if (!allSelected) model.statuses = selected;
    if (excludeTest) model.test = "Not test";
    if (excludeVoteBoxes) model.voteBoxes = false;
    return model;
  }
}

export function statusTestFilterHandler(): FilterHandler<unknown, unknown, StatusTestModel> {
  return {
    doesFilterPass(params: DoesFilterPassParams<unknown, unknown, StatusTestModel>): boolean {
      const model = params.model;
      if (!model) return true;
      const data = params.data as { status_label?: string; test?: boolean; row_type?: number };
      if (model.voteBoxes === false && data.row_type === 1) return false;
      if (data.row_type === 1) return true;
      if (model.statuses !== undefined && !model.statuses.includes(data.status_label ?? "")) return false;
      if (model.test === "Not test" && data.test === true) return false;
      if (model.test === "Test" && data.test !== true) return false;
      return true;
    },
    getModelAsString(model: StatusTestModel | null): string {
      if (!model) return "";
      const parts: string[] = [];
      if (model.statuses) parts.push(model.statuses.join(", "));
      if (model.test === "Not test") parts.push("excl. test");
      if (model.voteBoxes === false) parts.push("excl. vote boxes");
      return parts.join(" · ");
    },
  };
}
